import { RecurringStatus } from '../../lib/api/shopBanners';
import { ShopBannerLiveState } from '../../lib/types';

const COLORS: Record<ShopBannerLiveState, { fg: string; bg: string }> = {
  Draft: { fg: 'var(--ink-3)', bg: 'var(--bg-active)' },
  Scheduled: { fg: '#3B5CFF', bg: 'rgba(59, 92, 255, 0.10)' },
  'Live now': { fg: '#2B8A5A', bg: 'rgba(43, 138, 90, 0.12)' },
  Ended: { fg: 'var(--ink-4)', bg: 'var(--bg-subtle)' },
};

const pill = (fg: string, bg: string) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 9px',
  borderRadius: 999, fontSize: 11.5, fontWeight: 550, color: fg, background: bg,
  whiteSpace: 'nowrap' as const,
});

/** "Live now" / "Scheduled" / … plus a warning when the shop is behind mosy. */
export const ShopBannerStatus = ({
  state, inSync, notSetUp,
}: { state: ShopBannerLiveState; inSync: boolean | null; notSetUp?: boolean }) => {
  if (notSetUp) return <span style={pill('var(--ink-4)', 'var(--bg-subtle)')}>Not set up</span>;
  const c = COLORS[state];
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <span style={pill(c.fg, c.bg)}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
        {state}
      </span>
      {inSync === false && (
        <span style={pill('var(--warn)', 'var(--warn-bg)')} title="Edited after publishing — click Update shop to send the changes">
          Not in sync
        </span>
      )}
    </span>
  );
};

const RECURRING_COLORS: Record<RecurringStatus['state'], { fg: string; bg: string }> = {
  live: COLORS['Live now'],
  next: COLORS.Scheduled,
  paused: { fg: 'var(--warn)', bg: 'var(--warn-bg)' },
  off: COLORS.Ended,
  unpublished: COLORS.Draft,
};

/** Recurring banner: "Live now" / "Next: Mon 19:00" / "Paused until …" /
 *  "Off" / "Not published", plus "Not in sync". */
export const RecurringStatusPill = ({ status, inSync }: { status: RecurringStatus | null; inSync: boolean | null }) => {
  if (!status) return null;
  const c = RECURRING_COLORS[status.state];
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <span style={pill(c.fg, c.bg)}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
        {status.label}
      </span>
      {inSync === false && (
        <span style={pill('var(--warn)', 'var(--warn-bg)')} title="Edited after publishing — click Update shop to send the changes">
          Not in sync
        </span>
      )}
    </span>
  );
};

/** dd/mm (+ /yyyy when not this year) for a 'YYYY-MM-DD' string. */
export const fmtDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return y === String(new Date().getFullYear()) ? `${d}/${m}` : `${d}/${m}/${y}`;
};

export const dayBefore = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  return dt.toISOString().slice(0, 10);
};

/** "Live 01/01 00:00 → 10/01 00:00 (Brussels) · last full day 09/01". */
export const windowSentence = (start: string | null, end: string | null): string => {
  if (!start || !end) return 'Pick a start and an end date.';
  if (end <= start) return 'The end date must be after the start date.';
  return `Live ${fmtDay(start)} 00:00 → ${fmtDay(end)} 00:00 (Brussels) · last full day ${fmtDay(dayBefore(end))}`;
};

export default ShopBannerStatus;
